import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, Save, Plus, X } from "lucide-react";
import Layout from "components/ui/Layout";
import { useCreateSubscription } from "features/subscription-management/hooks/useSubsMutations";

const subscriptionSchema = z.object({
    nameEnglish: z.string().min(3, "English name is required"),
    nameHindi: z.string().optional(),
    nameMarwari: z.string().optional(),
    descriptionEnglish: z.string().min(10, "English description is required"),
    descriptionHindi: z.string().optional(),
    descriptionMarwari: z.string().optional(),
    basePrice: z.number().min(0, "Base price must be positive"),
    discountedPrice: z.number().min(0, "Discounted price must be positive"),
    currency: z.string().min(1, "Currency is required"),
    duration: z.number().min(1, "Duration must be positive"),
    durationType: z.enum(["days", "weeks", "months", "years"]),
    features: z.array(z.string()).min(1, "At least one feature is required"),
    maxUsers: z.number().min(1, "Maximum users must be positive"),
    trialDays: z.number().min(0, "Trial days must be positive"),
    isPopular: z.boolean(),
});

const AddSubscriptionPage = () => {
    const navigate = useNavigate();
    const { mutate: createSubscription, isPending } = useCreateSubscription();
    const [features, setFeatures] = useState([""]);
    const [newFeature, setNewFeature] = useState("");

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(subscriptionSchema),
        defaultValues: {
            nameEnglish: "",
            nameHindi: "",
            nameMarwari: "",
            descriptionEnglish: "",
            descriptionHindi: "",
            descriptionMarwari: "",
            basePrice: 0,
            discountedPrice: 0,
            currency: "INR",
            duration: 1,
            durationType: "months",
            features: [""],
            maxUsers: 1,
            trialDays: 0,
            isPopular: false,
        },
    });

    const durationType = watch("durationType");

    const addFeature = () => {
        if (newFeature.trim()) {
            const updatedFeatures = [...features, newFeature.trim()];
            setFeatures(updatedFeatures);
            setValue("features", updatedFeatures);
            setNewFeature("");
        }
    };

    const removeFeature = (index) => {
        const updatedFeatures = features.filter((_, i) => i !== index);
        setFeatures(updatedFeatures);
        setValue("features", updatedFeatures);
    };

    const updateFeature = (index, value) => {
        const updatedFeatures = [...features];
        updatedFeatures[index] = value;
        setFeatures(updatedFeatures);
        setValue("features", updatedFeatures);
    };

    const onSubmit = (data) => {
        const subscriptionData = {
            name: {
                english: data.nameEnglish,
                hindi: data.nameHindi,
                marwari: data.nameMarwari,
            },
            description: {
                english: data.descriptionEnglish,
                hindi: data.descriptionHindi,
                marwari: data.descriptionMarwari,
            },
            basePrice: Number(data.basePrice),
            discountedPrice: Number(data.discountedPrice),
            currency: data.currency,
            duration: Number(data.duration),
            durationType: data.durationType,
            features: data.features.filter(f => f.trim()),
            maxUsers: Number(data.maxUsers),
            trialDays: Number(data.trialDays),
            isPopular: data.isPopular,
        };

        createSubscription(subscriptionData, {
            onSuccess: () => {
                reset();
                setFeatures([""]);
                navigate('/subscription-management');
            },
        });
    };

    return (
        <Layout>
            <div className="min-h-screen bg-background">
                {/* Fixed Header */}
                <div className="sticky top-0 z-10 bg-card border-b border-border">
                    <div className="px-6 py-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={() => navigate('/subscription-management')}
                                    className="p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded transition-colors"
                                    title="Back to Subscription list"
                                >
                                    <ArrowLeft size={20} />
                                </button>
                                <div>
                                    <h3 className="text-lg font-semibold text-foreground">Add New Subscription Plan</h3>
                                    <p className="text-sm text-muted-foreground">Create a new subscription plan</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={() => navigate('/subscription-management')}
                                    className="px-4 py-2 text-sm border border-border text-foreground bg-card hover:bg-accent rounded-md transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    form="subscription-form"
                                    disabled={isPending}
                                    className="flex items-center gap-2 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
                                >
                                    <Save size={16} />
                                    {isPending ? 'Creating...' : 'Create Subscription Plan'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="p-6">
                    <div className="max-w-6xl mx-auto">
                        <form id="subscription-form" onSubmit={handleSubmit(onSubmit)}>
                            <div className="space-y-6">
                                {/* Names Section */}
                                <div className="bg-card border border-border rounded-lg p-6">
                                    <h4 className="text-md font-semibold text-foreground mb-4">Plan Names</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Plan Name (English) <span className="text-destructive">*</span>
                                            </label>
                                            <input
                                                {...register("nameEnglish")}
                                                className="w-full bg-input border border-border text-foreground rounded-md px-3 py-2 placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                                                placeholder="Premium Plan"
                                            />
                                            {errors.nameEnglish && (
                                                <p className="text-destructive text-xs mt-1">{errors.nameEnglish.message}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Plan Name (Hindi)
                                            </label>
                                            <input
                                                {...register("nameHindi")}
                                                className="w-full bg-input border border-border text-foreground rounded-md px-3 py-2 placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                                                placeholder="प्रीमियम योजना"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Plan Name (Marwari)
                                            </label>
                                            <input
                                                {...register("nameMarwari")}
                                                className="w-full bg-input border border-border text-foreground rounded-md px-3 py-2 placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                                                placeholder="प्रीमियम योजना"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Descriptions Section */}
                                <div className="bg-card border border-border rounded-lg p-6">
                                    <h4 className="text-md font-semibold text-foreground mb-4">Descriptions</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Description (English) <span className="text-destructive">*</span>
                                            </label>
                                            <textarea
                                                {...register("descriptionEnglish")}
                                                className="w-full bg-input border border-border text-foreground rounded-md px-3 py-2 h-20 placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                                                placeholder="Full access to all features"
                                            />
                                            {errors.descriptionEnglish && (
                                                <p className="text-destructive text-xs mt-1">{errors.descriptionEnglish.message}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Description (Hindi)
                                            </label>
                                            <textarea
                                                {...register("descriptionHindi")}
                                                className="w-full bg-input border border-border text-foreground rounded-md px-3 py-2 h-20 placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                                                placeholder="सभी सुविधाओं तक पूर्ण पहुंच"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Description (Marwari)
                                            </label>
                                            <textarea
                                                {...register("descriptionMarwari")}
                                                className="w-full bg-input border border-border text-foreground rounded-md px-3 py-2 h-20 placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                                                placeholder="सगळी सुविधा"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Pricing Section */}
                                <div className="bg-card border border-border rounded-lg p-6">
                                    <h4 className="text-md font-semibold text-foreground mb-4">Pricing & Duration</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Base Price <span className="text-destructive">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                {...register("basePrice", { valueAsNumber: true })}
                                                className="w-full bg-input border border-border text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                                                placeholder="499"
                                            />
                                            {errors.basePrice && (
                                                <p className="text-destructive text-xs mt-1">{errors.basePrice.message}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Discounted Price <span className="text-destructive">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                {...register("discountedPrice", { valueAsNumber: true })}
                                                className="w-full bg-input border border-border text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                                                placeholder="299"
                                            />
                                            {errors.discountedPrice && (
                                                <p className="text-destructive text-xs mt-1">{errors.discountedPrice.message}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Currency <span className="text-destructive">*</span>
                                            </label>
                                            <select
                                                {...register("currency")}
                                                className="w-full bg-input border border-border text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                                            >
                                                <option value="INR">INR</option>
                                                <option value="USD">USD</option>
                                                <option value="EUR">EUR</option>
                                            </select>
                                        </div>

                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Duration <span className="text-destructive">*</span>
                                                </label>
                                                <input
                                                    type="number"
                                                    {...register("duration", { valueAsNumber: true })}
                                                    className="w-full bg-input border border-border text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                                                    placeholder="3"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">Type</label>
                                                <select
                                                    {...register("durationType")}
                                                    className="w-full bg-input border border-border text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                                                >
                                                    <option value="days">Days</option>
                                                    <option value="months">Months</option>
                                                    <option value="years">Years</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Additional Settings */}
                                <div className="bg-card border border-border rounded-lg p-6">
                                    <h4 className="text-md font-semibold text-foreground mb-4">Additional Settings</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Max Users <span className="text-destructive">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                {...register("maxUsers", { valueAsNumber: true })}
                                                className="w-full bg-input border border-border text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                                                placeholder="5"
                                            />
                                            {errors.maxUsers && (
                                                <p className="text-destructive text-xs mt-1">{errors.maxUsers.message}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">Trial Days</label>
                                            <input
                                                type="number"
                                                {...register("trialDays", { valueAsNumber: true })}
                                                className="w-full bg-input border border-border text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                                                placeholder="7"
                                            />
                                        </div>

                                        <div className="flex items-center gap-2 pt-6">
                                            <input
                                                type="checkbox"
                                                {...register("isPopular")}
                                                className="w-4 h-4 text-primary bg-input border-border rounded focus:ring-primary"
                                            />
                                            <span className="text-sm text-foreground">Mark as Popular Plan</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Features Section */}
                                <div className="bg-card border border-border rounded-lg p-6">
                                    <h4 className="text-md font-semibold text-foreground mb-4">
                                        Features <span className="text-destructive">*</span>
                                    </h4>
                                    <div className="space-y-2">
                                        {features.map((feature, index) => (
                                            <div key={index} className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={feature}
                                                    onChange={(e) => updateFeature(index, e.target.value)}
                                                    className="flex-1 bg-input border border-border text-foreground rounded-md px-3 py-2 placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                                                    placeholder="Unlimited Projects"
                                                />
                                                {features.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeFeature(index)}
                                                        className="p-2 text-destructive hover:bg-destructive/10 rounded-md transition-colors"
                                                    >
                                                        <X size={20} />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex gap-2 mt-4">
                                        <input
                                            type="text"
                                            value={newFeature}
                                            onChange={(e) => setNewFeature(e.target.value)}
                                            className="flex-1 bg-input border border-border text-foreground rounded-md px-3 py-2 placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                                            placeholder="Add new feature"
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    addFeature();
                                                }
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={addFeature}
                                            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                                        >
                                            <Plus size={16} />
                                            Add Feature
                                        </button>
                                    </div>
                                    {errors.features && (
                                        <p className="text-destructive text-xs mt-2">{errors.features.message}</p>
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default AddSubscriptionPage;
